// DashboardPage.js - ACTUALIZADO con Finanzas (Ventas + Compras)
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  List,
  ListItem,
  Divider,
  Chip,
  Avatar,
  useTheme,
} from "@mui/material";
import {
  People,
  ContactMail,
  Description,
  Article,
  Engineering,
  Receipt,
  Assignment,
  ArrowForward,
  Business,
  LocalShipping,
  Handshake,
  AccountBalance,
  Sell,
  ShoppingCart,
} from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import { useCollection } from "../hooks/useCollection";
import DashboardStats from "../components/dashboard/DashboardStats";
import QuickActions from "../components/dashboard/QuickActions";
import RecentActivity from "../components/dashboard/RecentActivity";
import UpcomingEvents from "../components/dashboard/UpcomingEvents";
import PendingTasks from "../components/dashboard/PendingTasks";
import UpcomingSwitches from "../components/dashboard/UpcomingSwitches";

const DashboardPage = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const { data: clients } = useCollection("clients");
  const { data: contacts } = useCollection("contacts");
  const { data: proposals } = useCollection("proposals");
  const { data: contracts } = useCollection("contracts");
  const { data: projects } = useCollection("projects");
  const { data: salesInvoices } = useCollection("salesInvoices");
  const { data: purchaseInvoices } = useCollection("purchaseInvoices");
  const { data: companies } = useCollection("companies");
  const { data: suppliers } = useCollection("suppliers");
  const { data: commercials } = useCollection("commercials");

  const activeClients =
    clients?.filter((c) => c.isActive !== false)?.length || 0;
  const activeCompanies =
    companies?.filter((c) => c.isActive !== false)?.length || 0;
  const activeContacts =
    contacts?.filter((c) => c.isActive !== false)?.length || 0;
  const activeSuppliers =
    suppliers?.filter((s) => s.isActive !== false)?.length || 0;
  const activeCommercials =
    commercials?.filter((c) => c.isActive !== false)?.length || 0;
  const pendingProposals =
    proposals?.filter((p) => ["pending", "created", "sent"].includes(p.status))
      ?.length || 0;
  const activeContracts =
    contracts?.filter((c) => ["created", "sent"].includes(c.status))?.length ||
    0;
  const activeProjects =
    projects?.filter((p) => p.status !== "completed")?.length || 0;

  // NUEVO: facturas separadas
  const pendingSalesInvoices =
    salesInvoices?.filter((i) => ["draft", "sent"].includes(i.status))
      ?.length || 0;
  const pendingPurchaseInvoices =
    purchaseInvoices?.filter((i) =>
      ["draft", "received", "approved"].includes(i.status),
    )?.length || 0;

  const menuGroups = [
    {
      title: "Contactos",
      icon: <People fontSize="large" />,
      color: theme.palette.primary.main,
      bgColor:
        theme.palette.mode === "dark"
          ? "rgba(0, 175, 163, 0.15)"
          : "rgba(0, 95, 101, 0.08)",
      items: [
        {
          label: "Empresas",
          count: activeCompanies,
          path: "/companies",
          icon: <Business />,
        },
        {
          label: "Clientes",
          count: activeClients,
          path: "/clients",
          icon: <People />,
        },
        {
          label: "Proveedores",
          count: activeSuppliers,
          path: "/suppliers",
          icon: <LocalShipping />,
        },
        {
          label: "Comerciales",
          count: activeCommercials,
          path: "/commercials",
          icon: <Handshake />,
        },
        {
          label: "Contactos",
          count: activeContacts,
          path: "/contacts",
          icon: <ContactMail />,
        },
      ],
    },
    {
      title: "Proyectos",
      icon: <Engineering fontSize="large" />,
      color: theme.palette.secondary.main,
      bgColor:
        theme.palette.mode === "dark"
          ? "rgba(0, 147, 93, 0.15)"
          : "rgba(0, 147, 93, 0.08)",
      items: [
        {
          label: "Propuestas",
          count: pendingProposals,
          path: "/proposals",
          icon: <Description />,
        },
        {
          label: "Contratos",
          count: activeContracts,
          path: "/contracts",
          icon: <Article />,
        },
        {
          label: "Proyectos en curso",
          count: activeProjects,
          path: "/projects",
          icon: <Assignment />,
        },
      ],
    },
    // NUEVO: Grupo Finanzas (sustituye Facturación)
    {
      title: "Finanzas",
      icon: <AccountBalance fontSize="large" />,
      color: "#1abc9c",
      bgColor:
        theme.palette.mode === "dark"
          ? "rgba(245, 124, 0, 0.15)"
          : "rgba(245, 124, 0, 0.08)",
      items: [
        {
          label: "Ventas pendientes",
          count: pendingSalesInvoices,
          path: "/sales",
          icon: <Sell />,
        },
        {
          label: "Compras pendientes",
          count: pendingPurchaseInvoices,
          path: "/purchases",
          icon: <ShoppingCart />,
        },
      ],
    },
  ];

  return (
    <Box sx={{ px: { xs: 0, sm: 0 } }}>
      {/* Cabecera con gradiente EPC */}
      <Box
        sx={{
          mb: { xs: 2, sm: 3, md: 4 },
          mt: { xs: 1, sm: 2, md: 3 },
          p: { xs: 2, sm: 2.5, md: 3 },
          borderRadius: { xs: 2, md: 3 },
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(0, 95, 101, 0.3) 0%, rgba(0, 175, 163, 0.2) 100%)"
              : "linear-gradient(135deg, rgba(0, 95, 101, 0.1) 0%, rgba(0, 175, 163, 0.05) 100%)",
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(0, 175, 163, 0.2)"
              : "rgba(0, 95, 101, 0.1)",
        }}
      >
        <Typography
          variant="h4"
          fontWeight={700}
          sx={{
            fontSize: { xs: "1.3rem", sm: "1.75rem", md: "2.125rem" },
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 0.5,
          }}
        >
          ¡Hola, {userProfile?.displayName || "Usuario"}!
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
        >
          Panel de control - Resumen general de EPCore
        </Typography>
      </Box>

      <Box
        sx={{
          mb: { xs: 2, sm: 3, md: 4 },
          mt: { xs: 1, sm: 2, md: 3 },
          p: { xs: 1.5, sm: 2.5, md: 3 },
          borderRadius: { xs: 2, md: 3 },
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(0, 95, 101, 0.3) 0%, rgba(0, 175, 163, 0.2) 100%)"
              : "linear-gradient(135deg, rgba(0, 95, 101, 0.1) 0%, rgba(0, 175, 163, 0.05) 100%)",
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(0, 175, 163, 0.2)"
              : "rgba(0, 95, 101, 0.1)",
          overflowX: "hidden",
        }}
      >
        <QuickActions />
      </Box>

      {/* Stats generales */}
      <DashboardStats />

      {/* Tareas y Eventos */}
      <Box
        sx={{
          display: "flex",
          gap: { xs: 1.5, sm: 2, md: 2.5 },
          flexWrap: "wrap",
          mb: { xs: 2, sm: 3, md: 4 },
          mt: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Box
          sx={{
            flex: "1 1 400px",
            minWidth: { xs: "100%", sm: "350px" },
            maxWidth: { xs: "100%", sm: "none" },
          }}
        >
          <PendingTasks />
        </Box>
        <Box
          sx={{
            flex: "1 1 400px",
            minWidth: { xs: "100%", sm: "350px" },
            maxWidth: { xs: "100%", sm: "none" },
          }}
        >
          <UpcomingEvents />
        </Box>
      </Box>

      {/* NUEVO: Próximos cambios de comercializadora */}
      <Box
        sx={{
          display: "flex",
          gap: { xs: 1.5, sm: 2, md: 2.5 },
          flexWrap: "wrap",
          mb: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Box
          sx={{
            flex: "1 1 400px",
            minWidth: { xs: "100%", sm: "350px" },
            maxWidth: { xs: "100%", sm: "none" },
          }}
        >
          <UpcomingSwitches />
        </Box>
      </Box>

      {/* Grupos de acceso rápido */}
      <Typography
        variant="h5"
        fontWeight={600}
        sx={{
          mt: { xs: 1, sm: 2 },
          mb: { xs: 1.5, sm: 2, md: 3 },
          fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" },
          color: theme.palette.primary.main,
        }}
      >
        Acceso Rápido
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: { xs: 1.5, sm: 2, md: 2.5 },
          flexWrap: "wrap",
          width: "100%",
        }}
      >
        {menuGroups.map((group) => (
          <Box
            key={group.title}
            sx={{
              flex: "1 1 350px",
              minWidth: { xs: "100%", sm: "320px" },
              maxWidth: { xs: "100%", sm: "none" },
            }}
          >
            <Card
              sx={{
                height: "100%",
                borderTop: 4,
                borderColor: group.color,
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: theme.shadows[8],
                  transform: { xs: "none", sm: "translateY(-4px)" },
                  borderColor: group.color,
                },
              }}
            >
              <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1, sm: 1.5 },
                    mb: { xs: 1.5, sm: 2, md: 2.5 },
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: group.bgColor,
                      color: group.color,
                      width: { xs: 40, sm: 48 },
                      height: { xs: 40, sm: 48 },
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: { xs: "1.5rem", sm: "2.1875rem" },
                          },
                        }}
                      >
                        {group.icon}
                      </Box>
                    </Box>
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      sx={{
                        fontSize: {
                          xs: "0.95rem",
                          sm: "1.1rem",
                          md: "1.25rem",
                        },
                      }}
                    >
                      {group.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
                    >
                      {group.items.reduce((sum, item) => sum + item.count, 0)}{" "}
                      elementos
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />

                <List dense disablePadding>
                  {group.items.map((item) => (
                    <ListItem key={item.label} disablePadding sx={{ mb: 0.5 }}>
                      <CardActionArea
                        onClick={() => navigate(item.path)}
                        sx={{
                          borderRadius: 2,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: group.bgColor,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: { xs: 1, sm: 1.5 },
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: { xs: 1, sm: 1.5 },
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: { xs: 30, sm: 36 },
                                height: { xs: 30, sm: 36 },
                                bgcolor: group.bgColor,
                                color: group.color,
                                flexShrink: 0,
                              }}
                            >
                              {item.icon}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="body1"
                                fontWeight={500}
                                sx={{
                                  fontSize: {
                                    xs: "0.8rem",
                                    sm: "0.9rem",
                                    md: "1rem",
                                  },
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {item.label}
                              </Typography>
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: { xs: 0.5, sm: 1 },
                              flexShrink: 0,
                            }}
                          >
                            <Chip
                              label={item.count}
                              size="small"
                              color={item.count > 0 ? "primary" : "default"}
                              variant={item.count > 0 ? "filled" : "outlined"}
                              sx={{
                                fontWeight: 600,
                                minWidth: { xs: 32, sm: 40 },
                                height: { xs: 24, sm: 28 },
                                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                              }}
                            />
                            <ArrowForward
                              fontSize="small"
                              sx={{
                                color: group.color,
                                fontSize: { xs: 16, sm: 20 },
                                transition: "transform 0.2s ease",
                                ".MuiCardActionArea-root:hover &": {
                                  transform: "translateX(4px)",
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      </CardActionArea>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>
      <Box sx={{ mb: { xs: 2, sm: 3, md: 4 }, mt: { xs: 2, sm: 3, md: 4 } }}>
        <RecentActivity />
      </Box>
    </Box>
  );
};

export default DashboardPage;
